import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Paperclip, Image as ImageIcon, FileText, Send, X } from "lucide-react";

/**
 * Shared attachment composer used by ChannelView and DmView.
 *
 * Flow:
 *   1. User clicks the paperclip → dropdown menu (Photo / Document).
 *   2. File picker opens with the appropriate `accept=` filter.
 *   3. Selected file → preview dialog with an optional caption input.
 *   4. On send, uploads the file and calls `onSend({ attachment, caption })`
 *      so the parent posts a single message with the caption + attachment.
 *
 * This matches WhatsApp's UX where you *always* see what you're about to
 * send before it lands in the chat.
 */
export default function AttachmentComposer({ onSend, disabled }) {
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [pending, setPending] = useState(null); // { file, previewUrl, kind }
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const openPicker = (kind) => {
    if (disabled) return;
    (kind === "image" ? imageInputRef : docInputRef).current?.click();
  };

  const onFilePicked = (kind) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File is too large (max 10 MB)");
      return;
    }
    const previewUrl = kind === "image" ? URL.createObjectURL(f) : null;
    setPending({ file: f, previewUrl, kind });
    setCaption("");
  };

  const cancel = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    setCaption("");
  };

  const confirmSend = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pending.file);
      const { data } = await api.post("/files/upload", fd);
      await onSend({ attachment: data, caption: caption.trim() });
      if (pending.previewUrl) URL.revokeObjectURL(pending.previewUrl);
      setPending(null);
      setCaption("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={onFilePicked("image")}
        data-testid="attach-image-input"
      />
      <input
        ref={docInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        className="hidden"
        onChange={onFilePicked("document")}
        data-testid="attach-doc-input"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="p-1.5 hover:bg-zinc-100 rounded disabled:opacity-40 disabled:pointer-events-none"
            aria-label="Attach"
            data-testid="attach-menu-btn"
          >
            <Paperclip className="w-4 h-4 text-zinc-500"/>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="w-44">
          <DropdownMenuItem onClick={() => openPicker("image")} data-testid="attach-menu-image">
            <ImageIcon className="w-4 h-4 mr-2"/> Photo &amp; video
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openPicker("document")} data-testid="attach-menu-document">
            <FileText className="w-4 h-4 mr-2"/> Document
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!pending} onOpenChange={(v) => !v && cancel()}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="attach-preview-dialog">
          <DialogHeader className="px-4 pt-4 pb-2 flex-row justify-between items-center space-y-0">
            <DialogTitle className="text-sm font-semibold">
              {pending?.kind === "image" ? "Send photo" : "Send document"}
            </DialogTitle>
            <button
              onClick={cancel}
              className="p-1 hover:bg-zinc-100 rounded"
              aria-label="Close"
              data-testid="attach-preview-cancel"
            >
              <X className="w-4 h-4 text-zinc-500"/>
            </button>
          </DialogHeader>

          <div className="px-4">
            {pending?.kind === "image" && pending?.previewUrl ? (
              <img
                src={pending.previewUrl}
                alt="Preview"
                className="w-full max-h-80 object-contain rounded-md bg-zinc-100"
                data-testid="attach-preview-image"
              />
            ) : (
              <div
                className="w-full rounded-md bg-zinc-100 border border-zinc-200 p-6 flex items-center gap-3"
                data-testid="attach-preview-doc"
              >
                <FileText className="w-8 h-8 text-zinc-500 shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{pending?.file?.name}</div>
                  <div className="text-xs text-zinc-500">
                    {(pending?.file?.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-4 pb-4 pt-3">
            <div className="flex items-end gap-2 border border-zinc-200 rounded-lg p-2 focus-within:border-zinc-400">
              <Input
                autoFocus
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); confirmSend(); } }}
                placeholder="Add a caption…"
                className="flex-1 border-none focus-visible:ring-0 shadow-none px-2"
                data-testid="attach-preview-caption"
              />
              <Button
                size="sm"
                onClick={confirmSend}
                disabled={busy}
                data-testid="attach-preview-send"
                className="bg-[var(--brand-accent)] text-white hover:bg-orange-700"
              >
                {busy ? "…" : <Send className="w-3.5 h-3.5"/>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
